import { encryptHash, validateEmail } from '@src/shared/utils';
import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LINK_COURIER_ERROR } from '../../services/api';
import { trackManagerCourierAddInputError } from '../../tracking/courier-card';
import { useKeycloakUserState } from '@src/shared/context/keycloak-user';
import PIEInput from '@src/shared/components/PIEInput';
import styles from './CourierDetailsActionAddInput.module.scss';

export interface ICourierDetailErrorProps {
  status: boolean;
  reason: string;
}

type TCourierDetailActionsAddInput = {
  onOutSideClickValidationCheck: ({ isValidated, data }: any) => void;
  clearForm?: boolean;
  isEmailExists: ICourierDetailErrorProps;
  inputSize?: 'small' | 'medium' | 'large';
};

export const CourierDetailsActionsAddInput: FC<
  TCourierDetailActionsAddInput
> = ({ onOutSideClickValidationCheck, clearForm, isEmailExists }) => {
  // Component State
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const { t } = useTranslation();
  const { restaurant } = useKeycloakUserState();

  /* SideEffects */
  useEffect(() => {
    if (clearForm) {
      setEmail('');
      setEmailError('');
      onOutSideClickValidationCheck({ isValidated: false, data: email });
    }
  }, [clearForm]);

  useEffect(() => {
    setEmailError(isEmailExists?.reason);
    isEmailExists.status
      ? trackManagerCourierAddInputError(
          restaurant?.country as string,
          restaurant?.language as string,
          encryptHash(email)
        )
      : null;
    onOutSideClickValidationCheck({ isValidated: false, data: email });
  }, [isEmailExists]);

  // Component Action Handlers
  const handleOnChange = (e: any) => {
    const value = e.target.value;
    setEmail(value);
    setEmailError('');
    onOutSideClickValidationCheck({ isValidated: !!value.length, data: value });
  };

  const handleOnBlur = (e: any) => {
    const value = e.target.value;

    if (value && !validateEmail(value)) {
      setEmailError(t(`addCourier.error_${LINK_COURIER_ERROR.INVALID_EMAIL}`));
      trackManagerCourierAddInputError(
        restaurant?.country as string,
        restaurant?.language as string,
        encryptHash(value)
      );
      onOutSideClickValidationCheck({ isValidated: false, data: email });
    } else {
      setEmailError('');
      onOutSideClickValidationCheck({
        isValidated: !!value.length,
        data: email,
      });
    }
  };

  return (
    <div className={styles.container_add_input}>
      <PIEInput
        maxlength={120}
        inputMode="email"
        showErrorContainer={emailError ? true : false}
        error={emailError}
        value={email}
        size="large"
        placeholder={t(`addCourier.inputPlaceholder`, 'Courier email')}
        onBlur={handleOnBlur}
        onChange={handleOnChange}
      />
    </div>
  );
};
